import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import { FaSearch } from "react-icons/fa";

/**
 *  The main navbar container. We use 'position: sticky'
 *  to keep it pinned. We dynamically switch background & blur
 *  based on whether we're at the top of the page.
 */
const Section = styled.div`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 1100;
  display: flex;
  justify-content: center;
  box-sizing: border-box;
  width: 100%;
  height: 63px;

  /* Smooth transition between solid & blurred */
  transition: background-color 0.3s, backdrop-filter 0.3s;

  /* Solid at top; blurred once user scrolls */
  background-color: ${({ isAtTop }) =>
    isAtTop ? "#1f1f1f" : "#1f1f1f"};
  backdrop-filter: ${({ isAtTop }) => (isAtTop ? "none" : "blur(10px)")};
  -webkit-backdrop-filter: ${({ isAtTop }) => (isAtTop ? "none" : "blur(10px)")};

  /* Optional border at the bottom of navbar */
  border-bottom: 1px solid
    ${({ isAtTop }) => (isAtTop ? "#2f2f2f" : "#2f2f2f")};
`;

const NavbarContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0 30px;
`;

const LogoContainer = styled(Link)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  text-decoration: none;
  color: #45a049;
  font-size: 1rem;
  font-weight: bold;

  &:hover {
    color: #3c8c42;
  }
`;

const PictureFrame = styled.div`
  height: 33px;
  width: 33px;
  border-radius: 50%;
  background-color: #2e2f2e;
  border: 2px solid #45a049;
  overflow: hidden;

  &:hover {
    border: 2px solid #3c8c42;
  }
`;

const LogoImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const LinksContainer = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const NavLink = styled(Link)`
  color: #aaaaaa;
  font-size: 0.85rem;
  text-decoration: none;
  transition: color 0.3s;

  &:hover {
    color: #45a049;
  }
`;

const SearchBoxContainer = styled.div`
  display: flex;
  align-items: center;
  position: relative;
  width: 200px;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 35px;
  border: 2px solid #2f2f2f;
  border-radius: 5px;
  background-color: #1f1f1f;
  color: #aaaaaa;
  padding: 0 10px 0 35px;
  font-size: 0.9rem;
  transition: border-color 0.3s;

  &:focus {
    outline: none;
    border-color: #45a049;
  }
`;

const MagnifyingGlassIcon = styled(FaSearch)`
  position: absolute;
  top: 50%;
  left: 10px;
  transform: translateY(-50%);
  color: #aaaaaa;
  font-size: 1rem;
  transition: color 0.3s;
`;

const Navbar = () => {
  const [isAtTop, setIsAtTop] = useState(true);

  useEffect(() => {
    const handleScroll = () => {
      // If scrollY is 0, user is at the top (solid).
      // Otherwise, user has scrolled (blurred).
      setIsAtTop(window.scrollY === 0);
    };

    // Check on mount, then listen for scroll changes
    handleScroll();
    window.addEventListener("scroll", handleScroll);

    // Cleanup on unmount
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <Section isAtTop={isAtTop}>
      <NavbarContainer>
        <LogoContainer to="/">
          <PictureFrame>
            <LogoImage
              src="/assets/pingcraftlogo.png"
              alt="Pingcraft Support Logo"
            />
          </PictureFrame>
          Pingcraft Docs
        </LogoContainer>

        <LinksContainer>
          {/* Example Nav Links if needed */}
          {/* <NavLink to="/some-page">Some Page</NavLink> */}
        </LinksContainer>

        <SearchBoxContainer>
          <MagnifyingGlassIcon />
          <SearchInput type="text" placeholder="Search docs..." />
        </SearchBoxContainer>
      </NavbarContainer>
    </Section>
  );
};

export default Navbar;
